import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { BookmarkSimple, Plus, X } from "@phosphor-icons/react";

const PRETTY = {
  status: (v) => (v === "open" ? "Open" : v === "closed_any" ? "Closed" : v.replace(/_/g, " ")),
  severity: (v) => v, client: (v) => v, team: (v) => (v === "mine" ? "My team" : "Team"), tag: (v) => `#${v}`,
  q: (v) => `"${v}"`, escalated: () => "Tier 2", assignee: () => "Unassigned",
  sla: (v) => (v === "breached" ? "SLA breached" : "SLA due soon"), classification: (v) => v,
  date_from: (v) => `from ${v}`, date_to: (v) => `to ${v}`,
};

// Strip defaults so only meaningful filters are stored / shown.
export const activeFilters = (f) =>
  Object.fromEntries(Object.entries(f).filter(([k, v]) => v && v !== "all" && v !== false));

export const describeFilters = (f) =>
  Object.entries(activeFilters(f)).map(([k, v]) => (PRETTY[k] ? PRETTY[k](v) : String(v))).join(" · ");

const sameFilters = (a, b) => JSON.stringify(activeFilters(a)) === JSON.stringify(activeFilters(b));

export function SavedViews({ filters, onApply, emptyFilters }) {
  const [views, setViews] = useState([]);
  const [name, setName] = useState("");
  const [open, setOpen] = useState(false);
  const load = () => api.get("/soc/views").then((r) => setViews(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const current = activeFilters(filters);
  const hasFilters = Object.keys(current).length > 0;
  const activeView = views.find((v) => sameFilters(v.filters, filters));

  const save = async () => {
    try {
      await api.post("/soc/views", { name: name.trim() || describeFilters(filters), filters: current });
      toast.success("View saved"); setName(""); setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save view"); }
  };
  const remove = async (v, e) => {
    e.stopPropagation();
    try { await api.delete(`/soc/views/${v.id}`); toast.success(`Removed "${v.name}"`); load(); }
    catch (e2) { toast.error(e2?.response?.data?.detail || "Could not delete view"); }
  };

  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid="soc-saved-views">
      <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-1"><BookmarkSimple size={12} /> Views</span>
      {views.length === 0 && <span className="text-[11px] text-muted-foreground">No saved views yet — set filters, then save.</span>}
      {views.map((v) => {
        const isActive = activeView?.id === v.id;
        return (
          <button key={v.id} type="button" data-testid={`soc-view-${v.id}`} title={describeFilters(v.filters)}
            onClick={() => onApply(isActive ? { ...emptyFilters } : { ...emptyFilters, ...v.filters })}
            className={`group inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wide pl-2.5 pr-1.5 h-8 rounded-sm border transition-all ${isActive ? "border-primary bg-primary/10 text-primary font-bold" : "border-border text-muted-foreground hover:border-foreground/40 hover:text-foreground"}`}>
            {v.name}
            <span role="button" aria-label={`Delete view ${v.name}`} data-testid={`soc-view-delete-${v.id}`} onClick={(e) => remove(v, e)}
              className="opacity-0 group-hover:opacity-100 hover:text-rag-red-fg transition-opacity p-0.5"><X size={11} /></span>
          </button>
        );
      })}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button data-testid="soc-save-view-btn" variant="outline" size="sm" disabled={!hasFilters || Boolean(activeView)}
            title={!hasFilters ? "Set some filters first" : activeView ? "This combination is already saved" : "Save current filters as a view"}
            className="rounded-sm h-8 font-mono text-[10px] uppercase tracking-wider">
            <Plus size={12} className="mr-1" /> Save view
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-72 rounded-sm p-3 space-y-2" align="start">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Save current filters</div>
          <div className="text-xs text-muted-foreground" data-testid="soc-save-view-summary">{describeFilters(filters)}</div>
          <Input data-testid="soc-save-view-name" value={name} onChange={(e) => setName(e.target.value)} placeholder={describeFilters(filters)}
            onKeyDown={(e) => { if (e.key === "Enter") save(); }} className="rounded-sm h-8 text-xs" />
          <Button data-testid="soc-save-view-confirm" onClick={save} size="sm" className="w-full rounded-sm font-mono text-[10px] uppercase h-8">Save</Button>
        </PopoverContent>
      </Popover>
    </div>
  );
}
